import React from 'react';
import { useLocation, NavLink } from 'react-router-dom';
import {
  LayoutDashboard, FileBarChart2, Building2, FolderKanban, Tags,
  ArrowDownToLine, ArrowUpFromLine, Lock, Target,
  Combine, Briefcase, Settings, BarChart3, ListChecks,
  GraduationCap, Repeat, Users, LogOut, Activity, FileDown, TrendingDown, Gauge,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const sections = [
  {
    title: 'Visão Geral',
    items: [
      { to: '/', label: 'Dashboard Executivo', icon: LayoutDashboard, end: true, mod: 'dashboard' },
      { to: '/consolidado', label: 'Consolidado do Grupo', icon: Combine, mod: 'consolidado' },
      { to: '/dre', label: 'DRE Gerencial', icon: FileBarChart2, mod: 'dre' },
      { to: '/fluxo-caixa', label: 'Fluxo de Caixa', icon: BarChart3, mod: 'fluxo-caixa' },
      { to: '/radar-anual', label: 'Radar Anual', icon: Activity, mod: 'fluxo-caixa' },
    ],
  },
  {
    title: 'Lançamentos',
    items: [
      { to: '/lancamentos', label: 'Receitas, Custos e Despesas', icon: ListChecks, mod: 'lancamentos' },
      { to: '/importar-fluxo', label: 'Importar Fluxo de Caixa', icon: ArrowDownToLine, mod: 'importar' },
    ],
  },
  {
    title: 'Operações',
    items: [
      { to: '/cursos', label: 'Cursos & Eventos', icon: GraduationCap, mod: 'cursos' },
      { to: '/contratos', label: 'Contratos Recorrentes', icon: Repeat, mod: 'contratos' },
      { to: '/contas-pagar', label: 'Contas a Pagar', icon: ArrowUpFromLine, mod: 'contas-pagar' },
      { to: '/contas-receber', label: 'Contas a Receber & Cobrança', icon: ArrowDownToLine, mod: 'cobranca' },
    ],
  },
  {
    title: 'Controladoria',
    items: [
      { to: '/controladoria/despesas', label: 'Gestão de Despesas', icon: TrendingDown, mod: 'controladoria-despesas' },
      { to: '/controladoria/custos', label: 'Gestão de Custos', icon: Gauge, mod: 'controladoria-custos' },
      { to: '/fechamento', label: 'Fechamento Mensal', icon: Lock, mod: 'fechamento' },
      { to: '/relatorios', label: 'Relatórios PDF', icon: FileDown, mod: 'relatorios' },
    ],
  },
  {
    title: 'Cadastros',
    items: [
      { to: '/empresas', label: 'Empresas', icon: Building2, mod: 'cadastros' },
      { to: '/fontes-receita', label: 'Fontes de Receita', icon: FolderKanban, mod: 'cadastros' },
      { to: '/centros-custo', label: 'Centros de Custo', icon: Briefcase, mod: 'cadastros' },
      { to: '/categorias', label: 'Categorias', icon: Tags, mod: 'cadastros' },
    ],
  },
  {
    title: 'Sistema',
    items: [
      { to: '/usuarios', label: 'Usuários', icon: Users, mod: 'usuarios' },
      { to: '/configuracoes', label: 'Configurações', icon: Settings, mod: 'cadastros' },
    ],
  },
];

export default function Sidebar() {
  const { user, isMaster, canAccessModule, logout } = useAuth();
  useLocation();

  return (
    <aside
      className="fixed top-0 left-0 h-screen w-64 bg-zinc-950 border-r border-zinc-900 flex flex-col z-40"
      data-testid="sidebar"
    >
      {/* Brand */}
      <div className="px-6 pt-7 pb-6 border-b border-zinc-900">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md flex items-center justify-center bg-gradient-to-br from-[#D4AF37] to-[#8B7220] shadow-lg shadow-amber-900/30">
            <span className="font-display font-bold text-zinc-950 text-sm tracking-tight">C</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold text-zinc-50 text-[15px] leading-none">CEAP BRASIL</div>
            <div className="text-[11px] text-zinc-500 mt-1 tracking-wider uppercase">Controladoria</div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        {sections.map((section) => {
          // Filtra itens por allowed_modules
          const visible = section.items.filter((it) => canAccessModule(it.mod));
          if (visible.length === 0) return null;
          return (
            <div key={section.title} className="mb-6">
              <div className="px-6 mb-2">
                <span className="overline text-zinc-600">{section.title}</span>
              </div>
              <ul className="space-y-0.5">
                {visible.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.end}
                        data-testid={`nav-${item.to.replace(/\//g, '-') || 'home'}`}
                        className={({ isActive }) =>
                          `relative flex items-center gap-3 px-6 py-2.5 text-[13px] transition-all duration-150 ${
                            isActive
                              ? 'text-zinc-50 bg-zinc-900/50 border-l-2 border-[#D4AF37]'
                              : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/40 border-l-2 border-transparent'
                          }`
                        }
                      >
                        <Icon size={15} strokeWidth={1.8} />
                        <span className="truncate">{item.label}</span>
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </nav>

      {/* User box + logout + logo CEAP no rodapé */}
      <div className="px-4 py-3 border-t border-zinc-900 space-y-2">
        {user && (
          <div className="flex items-center gap-2.5 px-2" data-testid="sidebar-user-info">
            <div className="w-8 h-8 rounded-full bg-amber-950/40 border border-amber-700/40 flex items-center justify-center shrink-0">
              <span className="text-[11px] font-semibold gold-text uppercase">
                {(user.name || user.email).slice(0, 2)}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] text-zinc-100 truncate" title={user.name}>{user.name}</div>
              <div className="text-[10px] text-zinc-500 truncate">
                {isMaster ? <span className="gold-text">MASTER · acesso total</span> : 'Acesso restrito'}
              </div>
            </div>
          </div>
        )}
        <button
          onClick={logout}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-[12px] text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900/60 rounded transition-colors"
          data-testid="btn-logout"
        >
          <LogOut size={13} /> Sair
        </button>

        {/* Logo CEAP no rodapé */}
        <div className="flex items-center justify-center pt-3 mt-1 border-t border-zinc-900" data-testid="sidebar-footer-logo">
          <img
            src="https://customer-assets.emergentagent.com/job_grupo-consolidado/artifacts/xo8xf0yu_GRUPO%20CEAP%20BRASIL%20%281%29.png"
            alt="Grupo CEAP BRASIL"
            className="h-12 object-contain opacity-90"
          />
        </div>
      </div>
    </aside>
  );
}
